import React, { useState, useEffect } from 'react'
import { supabase } from '../supabaseClient'

export default function WeeklyCheckIn({ session, profile, onComplete }) {
  const [form, setForm] = useState({
    weight_lbs: '',
    weight_available: true,
    bp_systolic: '',
    bp_diastolic: '',
    bp_available: false,
    cpap_days_used: 7,
    cpap_applicable: false,
    hba1c: '',
    hba1c_available: false,
    hdl: '',
    triglycerides: '',
    ldl: '',
    lipids_available: false,
    medication_days_taken: 7,
    medication_applicable: false,
  })
  const [saving, setSaving] = useState(false)
  const [error, setError] = useState(null)
  const [existingCheckin, setExistingCheckin] = useState(null)

  const weekStart = getMonday(new Date()).toISOString().split('T')[0]

  function getMonday(date) {
    const d = new Date(date)
    const day = d.getDay()
    const diff = d.getDate() - day + (day === 0 ? -6 : 1)
    return new Date(d.setDate(diff))
  }

  useEffect(() => {
    if (profile) {
      const medApplicable = profile.htn || profile.diabetes || profile.hyperlipidemia || false
      setForm(f => ({
        ...f,
        bp_available: profile.has_bp_cuff || false,
        cpap_applicable: (profile.has_sleep_apnea && profile.uses_cpap) || false,
        hba1c_available: profile.diabetes || false,
        medication_applicable: medApplicable,
      }))
    }
    fetchExisting()
  }, [profile])

  async function fetchExisting() {
    const { data } = await supabase
      .from('weekly_checkins')
      .select('*')
      .eq('user_id', session.user.id)
      .eq('week_start_date', weekStart)
      .single()
    if (data) {
      setExistingCheckin(data)
      setForm(f => ({ ...f, ...data }))
    }

    await autoCalculateMedicationDays()
  }

  async function autoCalculateMedicationDays() {
    const medApplicable = profile?.htn || profile?.diabetes || profile?.hyperlipidemia
    if (!medApplicable) return

    const { data: dailyData } = await supabase
      .from('daily_checkins')
      .select('checkin_date, medication_taken, medication_applicable')
      .eq('user_id', session.user.id)
      .gte('checkin_date', weekStart)
      .lte('checkin_date', new Date().toISOString().split('T')[0])
      .order('checkin_date', { ascending: true })

    if (!dailyData || dailyData.length === 0) return

    const daysTaken = dailyData.filter(d => d.medication_taken === true).length

    setForm(f => ({ ...f, medication_days_taken: daysTaken, medication_applicable: true }))
  }

  function ldlColor(val) {
    if (!val) return null
    const n = parseFloat(val)
    if (n < 70) return { color: '#085041', bg: '#E1F5EE', label: 'Optimal' }
    if (n <= 100) return { color: '#854F0B', bg: '#FAEEDA', label: 'Near optimal — check with your provider' }
    return { color: '#A32D2D', bg: '#FCEBEB', label: 'Above goal — please discuss with your provider' }
  }

  function calcWeeklyScoreUpdates(existingScore) {
    let updates = {}
    let extraPoints = 0
    let extraPossible = 0

    if (form.bp_available && form.bp_systolic) {
      const sys = parseInt(form.bp_systolic)
      let sbp_pts = 0
      if (sys < 120) sbp_pts = 10
      else if (sys < 130) sbp_pts = 8
      else if (sys < 140) sbp_pts = 5
      else if (sys < 160) sbp_pts = 2
      else sbp_pts = 0

      let dbp_pts = 0
      if (form.bp_diastolic) {
        const dia = parseInt(form.bp_diastolic)
        if (dia < 80) dbp_pts = 3
        else if (dia <= 90) dbp_pts = 1
        else dbp_pts = 0
      }

      updates.bp_pts = sbp_pts + dbp_pts
      updates.bp_included = true
      extraPoints += sbp_pts + dbp_pts
      extraPossible += 13
    } else {
      updates.bp_pts = 0
      updates.bp_included = false
    }

    if (form.weight_available && form.weight_lbs && profile?.weight_lbs_baseline) {
      const baseline = parseFloat(profile.weight_lbs_baseline)
      const current = parseFloat(form.weight_lbs)
      const pctLoss = ((baseline - current) / baseline) * 100
      let weight_pts = 0
      if (pctLoss >= 10) weight_pts = 10
      else if (pctLoss >= 3) weight_pts = 8
      else if (pctLoss >= 1) weight_pts = 5
      else if (pctLoss >= 0) weight_pts = 2
      updates.weight_pts = weight_pts
      updates.weight_included = true
      extraPoints += weight_pts
      extraPossible += 10
    } else {
      updates.weight_pts = 0
      updates.weight_included = false
    }

    if (form.hba1c_available && form.hba1c) {
      const a1c = parseFloat(form.hba1c)
      let diabetes_pts = 0
      if (a1c < 7.0) diabetes_pts = 10
      else if (a1c < 8.0) diabetes_pts = 7
      else if (a1c < 9.0) diabetes_pts = 4
      updates.diabetes_pts = diabetes_pts
      updates.diabetes_included = true
      extraPoints += diabetes_pts
      extraPossible += 10
    } else {
      updates.diabetes_pts = 0
      updates.diabetes_included = false
    }

    if (form.lipids_available && form.hdl && form.triglycerides) {
      const hdl = parseFloat(form.hdl)
      const trig = parseFloat(form.triglycerides)
      let lipids_pts = 0
      if (hdl >= 60 && trig < 150) lipids_pts = 5
      else if (hdl >= 40 && trig < 200) lipids_pts = 3
      else lipids_pts = 1
      updates.lipids_pts = lipids_pts
      updates.lipids_included = true
      extraPoints += lipids_pts
      extraPossible += 5
    } else {
      updates.lipids_pts = 0
      updates.lipids_included = false
    }

    if (form.cpap_applicable && form.cpap_days_used !== null) {
      const days = parseInt(form.cpap_days_used)
      let cpap_pts = 0
      if (days === 7) cpap_pts = 2.5
      else if (days >= 5) cpap_pts = 1.5
      else if (days >= 4) cpap_pts = 1
      updates.cpap_pts = cpap_pts
      updates.cpap_included = true
      extraPoints += cpap_pts
      extraPossible += 2.5
    }

    if (form.medication_applicable && form.medication_days_taken !== null) {
      const days = parseInt(form.medication_days_taken)
      let medication_pts = 0
      if (days === 7) medication_pts = 10
      else if (days === 6) medication_pts = 8
      else if (days === 5) medication_pts = 6
      else if (days === 4) medication_pts = 4
      else if (days === 3) medication_pts = 2
      else medication_pts = 0
      updates.medication_pts = medication_pts
      updates.medication_included = true
      extraPoints += medication_pts
      extraPossible += 10
    } else {
      updates.medication_pts = 0
      updates.medication_included = false
    }

    if (existingScore) {
      const dailyRaw = (existingScore.exercise_pts || 0) +
        (existingScore.sleep_hrs_pts || 0) +
        (existingScore.alcohol_pts || 0) +
        (existingScore.caffeine_pts || 0) +
        (existingScore.anticoag_pts || 0) +
        (existingScore.tobacco_pts || 0)

      const newRaw = dailyRaw + extraPoints
      const newPossible = extraPossible > 0 ? (existingScore.possible_points || 0) + extraPossible : (existingScore.possible_points || 0)
      const newScaled = newPossible > 0 ? Math.min(100, Math.round((newRaw / newPossible) * 100)) : Math.min(100, Math.round((newRaw / 45) * 100))

      updates.raw_points = newRaw
      updates.possible_points = newPossible > 0 ? newPossible : existingScore.possible_points
      updates.scaled_score = newScaled
    } else {
      updates.raw_points = extraPoints
      updates.possible_points = extraPossible
      updates.scaled_score = extraPossible > 0 ? Math.min(100, Math.round((extraPoints / extraPossible) * 100)) : 0
    }

    return updates
  }

  async function handleSubmit() {
    setSaving(true)
    setError(null)

    const { error: weeklyError } = await supabase
      .from('weekly_checkins')
      .upsert({
        user_id: session.user.id,
        week_start_date: weekStart,
        weight_lbs: form.weight_available ? parseFloat(form.weight_lbs) || null : null,
        weight_available: form.weight_available,
        bp_systolic: form.bp_available ? parseInt(form.bp_systolic) || null : null,
        bp_diastolic: form.bp_available ? parseInt(form.bp_diastolic) || null : null,
        bp_available: form.bp_available,
        cpap_days_used: form.cpap_applicable ? parseInt(form.cpap_days_used) : null,
        cpap_applicable: form.cpap_applicable,
        hba1c: form.hba1c_available ? parseFloat(form.hba1c) || null : null,
        hba1c_available: form.hba1c_available,
        hdl: form.lipids_available ? parseFloat(form.hdl) || null : null,
        triglycerides: form.lipids_available ? parseFloat(form.triglycerides) || null : null,
        ldl: form.lipids_available ? parseFloat(form.ldl) || null : null,
        lipids_available: form.lipids_available,
        medication_days_taken: form.medication_applicable ? parseInt(form.medication_days_taken) : null,
        medication_applicable: form.medication_applicable,
      }, { onConflict: 'user_id,week_start_date' })

    if (weeklyError) {
      setError(weeklyError.message)
      setSaving(false)
      return
    }

    const today = new Date().toISOString().split('T')[0]
    const { data: existingScore } = await supabase
      .from('afib_scores')
      .select('*')
      .eq('user_id', session.user.id)
      .eq('score_date', today)
      .single()

    const scoreUpdates = calcWeeklyScoreUpdates(existingScore)
    if (Object.keys(scoreUpdates).length > 0) {
      if (existingScore) {
        await supabase
          .from('afib_scores')
          .update({ ...scoreUpdates, weekly_completed: true })
          .eq('user_id', session.user.id)
          .eq('score_date', today)
      }
    }

    setSaving(false)
    onComplete()
  }

  const inputStyle = {
    width: '100%', padding: '9px 12px', borderRadius: 8,
    border: '1px solid #ddd', fontSize: 14, boxSizing: 'border-box'
  }

  const sectionStyle = {
    background: 'white', borderRadius: 12, padding: 16, marginBottom: 10
  }

  const labelStyle = {
    fontSize: 12, color: '#555', display: 'block', marginBottom: 4
  }

  return (
    <div>
      {existingCheckin && (
        <div style={{ background: '#E6F1FB', borderRadius: 10, padding: '10px 14px', marginBottom: 12, fontSize: 13, color: '#0C447C' }}>
          ℹ️ You already have a weekly check-in for this week. Saving will update it.
        </div>
      )}

      {/* Weight */}
      <div style={sectionStyle}>
        <div style={{ fontWeight: 700, fontSize: 14, marginBottom: 10, color: '#2C2C2C' }}>
          ⚖️ Weight this week
        </div>
        <label style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 10, fontSize: 13, cursor: 'pointer' }}>
          <input type="checkbox" checked={form.weight_available}
            onChange={e => setForm({ ...form, weight_available: e.target.checked })} />
          I have a scale available
        </label>
        {form.weight_available && (
          <div>
            <label style={labelStyle}>Current weight (lbs)</label>
            <input type="number" value={form.weight_lbs} style={inputStyle}
              onChange={e => setForm({ ...form, weight_lbs: e.target.value })}
              placeholder="e.g. 185" />
            {form.weight_lbs && profile?.weight_lbs_baseline && (
              <div style={{ marginTop: 8, fontSize: 12, color: '#555' }}>
                Change from baseline: <strong style={{
                  color: parseFloat(form.weight_lbs) < profile.weight_lbs_baseline ? '#085041' : '#A32D2D'
                }}>
                  {(parseFloat(form.weight_lbs) - profile.weight_lbs_baseline).toFixed(1)} lbs
                </strong>
              </div>
            )}
          </div>
        )}
      </div>

      {/* Blood pressure */}
      <div style={sectionStyle}>
        <div style={{ fontWeight: 700, fontSize: 14, marginBottom: 10, color: '#2C2C2C' }}>
          🩺 Blood pressure
        </div>
        {!profile?.has_bp_cuff ? (
          <div style={{ fontSize: 13, color: '#888' }}>
            No BP cuff on file. Update your profile if you have one.
          </div>
        ) : (
          <div>
            <label style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 10, fontSize: 13, cursor: 'pointer' }}>
              <input type="checkbox" checked={form.bp_available}
                onChange={e => setForm({ ...form, bp_available: e.target.checked })} />
              I took a reading this week
            </label>
            {form.bp_available && (
              <div>
                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10, marginBottom: 10 }}>
                  <div>
                    <label style={labelStyle}>Systolic (top number)</label>
                    <input type="number" value={form.bp_systolic} style={inputStyle}
                      onChange={e => setForm({ ...form, bp_systolic: e.target.value })}
                      placeholder="e.g. 122" />
                  </div>
                  <div>
                    <label style={labelStyle}>Diastolic (bottom number)</label>
                    <input type="number" value={form.bp_diastolic} style={inputStyle}
                      onChange={e => setForm({ ...form, bp_diastolic: e.target.value })}
                      placeholder="e.g. 78" />
                  </div>
                </div>

                {form.bp_systolic && (
                  <div style={{
                    padding: '8px 12px', borderRadius: 8, fontSize: 12, marginBottom: 6,
                    background: parseInt(form.bp_systolic) < 120 ? '#E1F5EE' : parseInt(form.bp_systolic) < 140 ? '#FAEEDA' : '#FCEBEB',
                    color: parseInt(form.bp_systolic) < 120 ? '#085041' : parseInt(form.bp_systolic) < 140 ? '#633806' : '#A32D2D'
                  }}>
                    Systolic: {parseInt(form.bp_systolic) < 120 ? '✅ Normal' :
                      parseInt(form.bp_systolic) < 130 ? '⚠️ Elevated' :
                      parseInt(form.bp_systolic) < 140 ? '⚠️ Stage 1 high' :
                      '🔴 Stage 2 high — contact your provider if persistent'}
                  </div>
                )}

                {form.bp_diastolic && (
                  <div style={{
                    padding: '8px 12px', borderRadius: 8, fontSize: 12,
                    background: parseInt(form.bp_diastolic) < 80 ? '#E1F5EE' : parseInt(form.bp_diastolic) <= 90 ? '#FAEEDA' : '#FCEBEB',
                    color: parseInt(form.bp_diastolic) < 80 ? '#085041' : parseInt(form.bp_diastolic) <= 90 ? '#633806' : '#A32D2D'
                  }}>
                    Diastolic: {parseInt(form.bp_diastolic) < 80 ? '✅ Normal' :
                      parseInt(form.bp_diastolic) <= 90 ? '⚠️ Elevated' :
                      '🔴 High — contact your provider if persistent'}
                  </div>
                )}
              </div>
            )}
          </div>
        )}
      </div>

      {(profile?.htn || profile?.diabetes || profile?.hyperlipidemia) && (
        <div style={sectionStyle}>
          <div style={{ fontWeight: 700, fontSize: 14, marginBottom: 6, color: '#2C2C2C' }}>
            💊 Cardiac medication compliance
          </div>
          <div style={{ background: '#E6F1FB', borderRadius: 8, padding: '8px 12px', marginBottom: 12, fontSize: 12, color: '#0C447C' }}>
            ℹ️ Auto-calculated from your daily check-ins this week. You can adjust if needed.
          </div>
          <div style={{ fontSize: 12, color: '#666', marginBottom: 12 }}>
            Days taken this week:
            {profile?.htn && ' blood pressure'}
            {profile?.diabetes && ' diabetes'}
            {profile?.hyperlipidemia && ' cholesterol'} medication
          </div>
          <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
            <input type="range" min="0" max="7" step="1"
              value={form.medication_days_taken}
              onChange={e => setForm({ ...form, medication_days_taken: parseInt(e.target.value) })}
              style={{ flex: 1 }} />
            <span style={{ fontSize: 16, fontWeight: 700, color: '#185FA5', minWidth: 36 }}>
              {form.medication_days_taken}/7
            </span>
          </div>
          <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 10, color: '#888', marginTop: 4 }}>
            <span>0 days</span><span>Goal: 7/7 days</span><span>7 days</span>
          </div>
          <div style={{
            marginTop: 10, padding: '8px 12px', borderRadius: 8, fontSize: 12,
            background: form.medication_days_taken === 7 ? '#E1F5EE' : form.medication_days_taken >= 5 ? '#FAEEDA' : '#FCEBEB',
            color: form.medication_days_taken === 7 ? '#085041' : form.medication_days_taken >= 5 ? '#633806' : '#A32D2D'
          }}>
            {form.medication_days_taken === 7 ? '✅ Perfect compliance this week' :
              form.medication_days_taken >= 5 ? '⚠️ Good but try to reach 7/7 days' :
              form.medication_days_taken >= 3 ? '⚠️ Inconsistent — missing doses increases your AFib and stroke risk' :
              '🔴 Very low compliance — please discuss with your provider'}
          </div>
          {form.medication_days_taken < 7 && (
            <div style={{ marginTop: 8, padding: '8px 12px', borderRadius: 8, fontSize: 12, background: '#FCEBEB', color: '#A32D2D', lineHeight: 1.6 }}>
              Missing cardiac medications increases your risk of uncontrolled blood pressure, diabetes complications, and AFib episodes. Please take your medications consistently and contact your provider if you have concerns about side effects.
            </div>
          )}
        </div>
      )}

      {/* CPAP */}
      {profile?.has_sleep_apnea && profile?.uses_cpap && (
        <div style={sectionStyle}>
          <div style={{ fontWeight: 700, fontSize: 14, marginBottom: 10, color: '#2C2C2C' }}>
            😷 CPAP use this week
          </div>
          <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
            <input type="range" min="0" max="7" step="1" value={form.cpap_days_used}
              onChange={e => setForm({ ...form, cpap_days_used: parseInt(e.target.value) })}
              style={{ flex: 1 }} />
            <span style={{ fontSize: 16, fontWeight: 700, color: '#185FA5', minWidth: 24 }}>
              {form.cpap_days_used}
            </span>
          </div>
          <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 10, color: '#888', marginTop: 4 }}>
            <span>0 nights</span><span>Goal: 7 nights</span><span>7 nights</span>
          </div>
        </div>
      )}

      {/* HbA1c */}
      {profile?.diabetes && (
        <div style={sectionStyle}>
          <div style={{ fontWeight: 700, fontSize: 14, marginBottom: 10, color: '#2C2C2C' }}>
            🩸 HbA1c (diabetes control)
          </div>
          <label style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 10, fontSize: 13, cursor: 'pointer' }}>
            <input type="checkbox" checked={form.hba1c_available}
              onChange={e => setForm({ ...form, hba1c_available: e.target.checked })} />
            I have a recent HbA1c result
          </label>
          {form.hba1c_available && (
            <div>
              <label style={labelStyle}>HbA1c value (%)</label>
              <input type="number" step="0.1" value={form.hba1c} style={inputStyle}
                onChange={e => setForm({ ...form, hba1c: e.target.value })}
                placeholder="e.g. 7.2" />
            </div>
          )}
        </div>
      )}

      {/* Lipids */}
      <div style={sectionStyle}>
        <div style={{ fontWeight: 700, fontSize: 14, marginBottom: 10, color: '#2C2C2C' }}>
          🧪 Cholesterol / lipids
        </div>
        <label style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 10, fontSize: 13, cursor: 'pointer' }}>
          <input type="checkbox" checked={form.lipids_available}
            onChange={e => setForm({ ...form, lipids_available: e.target.checked })} />
          I have recent lab results
        </label>
        {form.lipids_available && (
          <div>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10, marginBottom: 10 }}>
              <div>
                <label style={labelStyle}>HDL (mg/dL)</label>
                <input type="number" value={form.hdl} style={inputStyle}
                  onChange={e => setForm({ ...form, hdl: e.target.value })}
                  placeholder="e.g. 55" />
              </div>
              <div>
                <label style={labelStyle}>Triglycerides (mg/dL)</label>
                <input type="number" value={form.triglycerides} style={inputStyle}
                  onChange={e => setForm({ ...form, triglycerides: e.target.value })}
                  placeholder="e.g. 140" />
              </div>
            </div>

            <div style={{ marginBottom: 10 }}>
              <label style={labelStyle}>LDL (mg/dL) — optional</label>
              <input type="number" value={form.ldl} style={inputStyle}
                onChange={e => setForm({ ...form, ldl: e.target.value })}
                placeholder="e.g. 85" />
              {form.ldl && ldlColor(form.ldl) && (
                <div style={{
                  marginTop: 8, padding: '8px 12px', borderRadius: 8, fontSize: 12,
                  background: ldlColor(form.ldl).bg,
                  color: ldlColor(form.ldl).color,
                  fontWeight: 600
                }}>
                  LDL {form.ldl} mg/dL — {ldlColor(form.ldl).label}
                </div>
              )}
            </div>
          </div>
        )}
      </div>

      {error && (
        <div style={{ color: '#A32D2D', fontSize: 13, marginBottom: 10, padding: '8px 12px', background: '#FCEBEB', borderRadius: 8 }}>
          {error}
        </div>
      )}

      <button onClick={handleSubmit} disabled={saving} style={{
        width: '100%', padding: 14, background: saving ? '#9CB8D9' : '#185FA5',
        color: 'white', border: 'none', borderRadius: 10, fontSize: 15,
        fontWeight: 700, cursor: saving ? 'not-allowed' : 'pointer'
      }}>
        {saving ? 'Saving...' : 'Save weekly check-in'}
      </button>
    </div>
  )
}